import {
    EmployeeFilesDashboardResponse,
    UserEmployeeFilesResponse,
    EmployeeFile
} from "../../interfaces/employeeFiles/EmployeeFiles";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

export async function getEmployeeFilesDashboard(
    authToken: string,
    page: number = 1,
    pageSize: number = 10,
    searchTerm: string = ""
): Promise<EmployeeFilesDashboardResponse | null> {
    try {
        const queryParams = new URLSearchParams({
            page: page.toString(),
            pageSize: pageSize.toString(),
        });

        if (searchTerm) {
            queryParams.append("searchTerm", searchTerm);
        }

        const apiUrl = `${API_BASE_URL}/employee-file?${queryParams.toString()}`;

        const response = await fetch(apiUrl, {
            headers: {
                Authorization: `Bearer ${authToken}`,
                "Content-Type": "application/json",
            },
        });

        if (!response.ok) {
            console.error(`Error fetching employee files dashboard: ${response.status} ${response.statusText}`);
            return null;
        }

        const jsonResponse = await response.json();

        // FIX: Extract the inner 'data' property from the ApiResponse wrapper
        const payload = jsonResponse.data;

        if (!payload) return null;

        return {
            // Add safety check (|| []) in case myFiles is null
            myFiles: (payload.myFiles || []).map((f: any) => mapToEmployeeFile(f)),
            totalMyFilesCount: payload.totalMyFilesCount || 0,
            managedEmployees: payload.managedEmployees || []
        };

    } catch (error) {
        console.error("Error in getEmployeeFilesDashboard:", error);
        return null;
    }
}

export async function getEmployeeFilesForUser(
    authToken: string,
    targetUserId: string,
    page: number = 1,
    pageSize: number = 10,
    searchTerm: string = ""
): Promise<UserEmployeeFilesResponse | null> {
    try {
        const queryParams = new URLSearchParams({
            page: page.toString(),
            pageSize: pageSize.toString(),
        });

        if (searchTerm) {
            queryParams.append("searchTerm", searchTerm);
        }

        const apiUrl = `${API_BASE_URL}/employee-file/user/${targetUserId}?${queryParams.toString()}`;

        const response = await fetch(apiUrl, {
            headers: {
                Authorization: `Bearer ${authToken}`,
                "Content-Type": "application/json",
            },
        });

        if (response.status === 403) {
            console.warn("Unauthorized access attempt to user files.");
            throw new Error("Unauthorized: You do not have permission to view this user's files.");
        }

        if (!response.ok) {
            console.error(`Error fetching user files: ${response.status}`);
            return null;
        }

        const jsonResponse = await response.json();

        // FIX: Extract the inner 'data' property here as well
        const payload = jsonResponse.data;

        if (!payload) return null;

        return {
            files: (payload.files || []).map((f: any) => mapToEmployeeFile(f)),
            totalCount: payload.totalCount || 0
        };

    } catch (error) {
        console.error("Error in getEmployeeFilesForUser:", error);
        throw error;
    }
}

function mapToEmployeeFile(data: any): EmployeeFile {
    return {
        ...data,
        dateAdded: new Date(data.dateAdded),
        documentCategory: data.documentCategory ? { ...data.documentCategory } : undefined
    };
}

export async function getEmployeeFileMetadata(
    authToken: string,
    fileId: string
): Promise<EmployeeFile | null> {
    try {
        const apiUrl = `${API_BASE_URL}/employee-file/${fileId}`;

        const response = await fetch(apiUrl, {
            headers: {
                Authorization: `Bearer ${authToken}`,
                "Content-Type": "application/json",
            },
        });

        if (!response.ok) {
            console.error(`Error fetching file metadata: ${response.status}`);
            return null;
        }

        const data = await response.json();

        // Handle Wrapped Response if necessary, otherwise map directly
        const fileData = data.data || data;

        return {
            ...fileData,
            dateAdded: new Date(fileData.dateAdded),
            documentCategory: fileData.documentCategory ? { ...fileData.documentCategory } : undefined
        };

    } catch (error) {
        console.error("Error in getEmployeeFileMetadata:", error);
        return null;
    }
}

/**
 * Downloads the actual file content (Blob) using the new tableType endpoint.
 * Backend signature: GetEmployeeFile(string tableType, string fileId)
 */
export async function downloadEmployeeFileBlob(
    authToken: string,
    fileId: string
): Promise<Blob | null> {
    try {
        const apiUrl = `${API_BASE_URL}/employee-file/download/${fileId}`;

        const response = await fetch(apiUrl, {
            method: "GET",
            headers: {
                Authorization: `Bearer ${authToken}`,
            },
        });

        if (!response.ok) {
            console.error(`Error downloading file blob: ${response.status}`);
            return null;
        }

        return await response.blob();
    } catch (error) {
        console.error("Error in downloadEmployeeFileBlob:", error);
        return null;
    }
}

/**
 * Helper to get a temporary Object URL for viewing the file in the browser.
 */
export async function getEmployeeFileViewUrl(
    authToken: string,
    fileId: string
): Promise<string | null> {
    const blob = await downloadEmployeeFileBlob(authToken, fileId);
    if (blob) {
        return URL.createObjectURL(blob);
    }
    return null;
}